import React, { useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  addHoliday,
  deleteHoliday,
  getHolidayRecords,
  getHolidayStatus,
  HolidaySyncOutcome,
  slackActions,
  syncHolidays,
  updateHoliday,
} from '@/store/actions/slack';
import { selectHolidayRecords, selectHolidayStatus } from '@/store/selectors/slack';
import { HolidayRecord, HolidayStatus, PollSummary } from '@/store/reducers/slack';

import useLanguage from '@/components/common/hooks/useLanguage';
import BackIcon from '@/components/common/icons/BackIcon';
import Loader from '@/components/common/Loader';
import Empty from '@/components/Empty';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const pretty = (iso: string) => iso.split('-').reverse().join('.');

// Poll times are Kyiv times whatever the browser's zone, like the schedule itself.
const prettyTime = (ms: number) => new Date(ms).toLocaleString('uk-UA', {
  timeZone: 'Europe/Kiev',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

type Text = ReturnType<typeof useLanguage>[0]['holidays'];

const describeSources = (text: Text, sources: Record<string, number>) => Object
  .entries(sources)
  .map(([source, count]) => `${count} ${text.source(source)}`)
  .join(', ');

const pollCounts = (text: Text, poll: PollSummary) => [
  text.resting(poll.rest),
  text.working(poll.work),
  poll.noAnswer !== undefined ? text.no_answer(poll.noAnswer) : '',
  poll.unmapped ? text.unmapped(poll.unmapped) : '',
].filter(Boolean).join(' · ');

interface Draft {
  title: string;
  date: string;
  observedDate: string;
}

/**
 * The admin screen for holidays, reached from /me: the calendar's coverage and the
 * work.ua pull, the Slack poll's schedule, and the calendar itself.
 *
 * Editing matters most for a year pulled automatically from work.ua: that page
 * publishes nominal dates only, so the carried-over day is derived rather than sourced
 * and wants a human eye.
 *
 * Slack accounts are bound per person, on the employee page -- not here.
 */
const Holidays: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [{ holidays: text, inputsPages, common }] = useLanguage();

  const holidays = useSelector(selectHolidayRecords);
  const status = useSelector(selectHolidayStatus);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<HolidaySyncOutcome | null>(null);

  const [showAllHolidays, setShowAllHolidays] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>({ title: '', date: '', observedDate: '' });
  const [added, setAdded] = useState<Draft>({ title: '', date: '', observedDate: '' });

  // A layout effect, so the stored copies render before the first paint -- see
  // store/responses.ts.
  useLayoutEffect(() => {
    dispatch(getHolidayRecords());
    dispatch(getHolidayStatus());

    // A first visit has nothing stored; without the wipe the previous screen's rows
    // would show instead. Only the holidays and their status are cleared -- the Slack
    // directory is shared with the employee page's picker and is not this screen's to
    // drop.
    return () => {
      dispatch(slackActions.cleanHolidays());
    };
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  // The whole current year by default, not only what is still ahead: by September only
  // two of eleven dates remain, which read as a calendar that failed to import. The
  // dates already gone by are greyed out.
  const visibleHolidays = useMemo(() => {
    const yearStart = `${today.slice(0, 4)}-01-01`;

    return (holidays || [])
      .filter((holiday) => showAllHolidays || holiday.observedDate >= yearStart)
      .sort((a, b) => (showAllHolidays ? -1 : 1) * a.observedDate.localeCompare(b.observedDate));
  }, [holidays, showAllHolidays, today]);

  const startEdit = (holiday: HolidayRecord) => {
    setEditingId(holiday._id);
    setDraft({
      title: holiday.title,
      date: holiday.date,
      observedDate: holiday.observedDate,
    });
  };

  const isValid = (d: Draft) => Boolean(d.title.trim())
    && ISO_DATE.test(d.date)
    && (!d.observedDate || ISO_DATE.test(d.observedDate));

  const saveEdit = () => {
    if (editingId === null || !isValid(draft)) return;

    dispatch(updateHoliday(editingId, {
      title: draft.title.trim(),
      date: draft.date,
      observedDate: draft.observedDate || draft.date,
    }));
    setEditingId(null);
  };

  const handleAdd = () => {
    if (!isValid(added)) return;

    dispatch(addHoliday({
      title: added.title.trim(),
      date: added.date,
      observedDate: added.observedDate || undefined,
    }));
    setAdded({ title: '', date: '', observedDate: '' });
  };

  const handleSync = async (year: number) => {
    setIsSyncing(true);
    setSyncResult(null);

    const result = await (
      dispatch(syncHolidays(year)) as unknown as Promise<HolidaySyncOutcome>
    );

    setSyncResult(result);
    setIsSyncing(false);
  };

  const header = (
    <>
      <Header>
        <IconWrap onClick={() => history.goBack()}><BackIcon /></IconWrap>
        <HeaderText>{text.title}</HeaderText>
      </Header>
      <Empty height="72px" />
    </>
  );

  // Spinner tests data presence only. A flag would be true through every background
  // refresh and would blank the screen.
  if (!holidays) {
    return (
      <>
        {header}
        <Loader scale="0.5" />
      </>
    );
  }

  return (
    <>
      {header}

      <Content className="scrollbar">
        {status && (
          <StatusSections
            text={text}
            status={status}
            isSyncing={isSyncing}
            syncResult={syncResult}
            onSync={handleSync}
          />
        )}

        <Section>
          <SectionName>{text.list_title(visibleHolidays.length)}</SectionName>
          <Hint>{text.list_hint}</Hint>
          <Toggle onClick={() => setShowAllHolidays((v) => !v)}>
            {showAllHolidays ? text.show_this_year : text.show_all_years}
          </Toggle>

          {visibleHolidays.length === 0
            ? <Muted>{text.nothing_ahead}</Muted>
            : visibleHolidays.map((holiday) => (editingId === holiday._id ? (
              <EditRow key={holiday._id}>
                <Field
                  placeholder={text.holiday_name}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
                <Labelled>
                  <FieldLabel>{text.holiday_date}</FieldLabel>
                  <Field
                    type="date"
                    value={draft.date}
                    onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  />
                </Labelled>
                <Labelled>
                  <FieldLabel>{text.day_off_label}</FieldLabel>
                  <Field
                    type="date"
                    value={draft.observedDate}
                    onChange={(e) => setDraft({ ...draft, observedDate: e.target.value })}
                  />
                </Labelled>
                <Action disabled={!isValid(draft)} onClick={saveEdit}>{inputsPages.save}</Action>
                <Action onClick={() => setEditingId(null)}>{common.cancel}</Action>
              </EditRow>
            ) : (
              <Row key={holiday._id} isPast={holiday.observedDate < today}>
                <Who>
                  <Name>{holiday.title}</Name>
                  <Sub>
                    {holiday.observedDate === holiday.date
                      ? pretty(holiday.date)
                      : text.moved_to(pretty(holiday.date), pretty(holiday.observedDate))}
                    {holiday.source === 'work.ua' ? text.check_transfer : ''}
                  </Sub>
                </Who>
                <Action onClick={() => startEdit(holiday)}>{inputsPages.edit}</Action>
                <Action onClick={() => dispatch(deleteHoliday(holiday._id))}>{common.delete}</Action>
              </Row>
            )))}

          <EditRow>
            <Field
              placeholder={text.new_holiday}
              value={added.title}
              onChange={(e) => setAdded({ ...added, title: e.target.value })}
            />
            <Labelled>
              <FieldLabel>{text.holiday_date}</FieldLabel>
              <Field
                type="date"
                value={added.date}
                onChange={(e) => setAdded({ ...added, date: e.target.value })}
              />
            </Labelled>
            <Labelled>
              <FieldLabel>{text.day_off_if_moved}</FieldLabel>
              <Field
                type="date"
                value={added.observedDate}
                onChange={(e) => setAdded({ ...added, observedDate: e.target.value })}
              />
            </Labelled>
            <Action disabled={!isValid(added)} onClick={handleAdd}>{inputsPages.add}</Action>
          </EditRow>
        </Section>

      </Content>
    </>
  );
};

interface StatusProps {
  text: Text;
  status: HolidayStatus;
  isSyncing: boolean;
  syncResult: HolidaySyncOutcome | null;
  onSync: (year: number) => void;
}

const StatusSections: React.FC<StatusProps> = ({
  text, status, isSyncing, syncResult, onSync,
}) => {
  const { calendar, nextPoll, openPolls, lastClosedPoll, slack } = status;

  const syncMessage = () => {
    if (!syncResult) return '';
    if (!syncResult.ok) {
      return syncResult.message ? `${text.sync_failed}: ${syncResult.message}` : text.sync_failed;
    }

    return syncResult.added
      ? text.sync_added(syncResult.year, syncResult.added, syncResult.found)
      : text.sync_nothing_new(syncResult.year, syncResult.found);
  };

  return (
    <>
      <Section>
        <SectionName>{text.calendar_sync}</SectionName>
        {calendar.years.map(({ year, total, sources }) => (
          <StatusRow key={year}>
            <Name>{text.year_dates(year, total)}</Name>
            <Sub>{describeSources(text, sources)}</Sub>
          </StatusRow>
        ))}
        <StatusRow>
          <Name>
            {calendar.hasNextYear
              ? text.next_year_present(calendar.nextYear)
              : text.next_year_missing(calendar.nextYear)}
          </Name>
          {!calendar.hasNextYear && (
            <Sub>
              {calendar.isNextYearDue
                ? text.auto_sync_running
                : text.auto_sync_from(pretty(calendar.autoSyncFrom))}
            </Sub>
          )}
        </StatusRow>
        <Action disabled={isSyncing} onClick={() => onSync(calendar.nextYear)}>
          {isSyncing ? text.syncing : text.sync_now(calendar.nextYear)}
        </Action>
        {syncResult && (
          <Hint style={{ color: syncResult.ok ? '#27ae60' : '#eb5757' }}>
            {syncMessage()}
          </Hint>
        )}
        <Hint>{text.sync_hint}</Hint>
      </Section>

      <Section>
        <SectionName>{text.slack_poll}</SectionName>
        <StatusRow>
          <Name>{text.channel(slack.channel)}</Name>
          {!slack.botActive && <Warning>{text.bot_muted}</Warning>}
        </StatusRow>
        {nextPoll ? (
          <StatusRow>
            <Name>{text.next_poll(nextPoll.holiday.title)}</Name>
            <Sub>{text.day_off(pretty(nextPoll.holiday.observedDate))}</Sub>
            {nextPoll.isOverdue
              ? <Warning>{text.poll_overdue(pretty(nextPoll.postOn))}</Warning>
              : <Sub>{text.poll_posts_on(pretty(nextPoll.postOn))}</Sub>}
          </StatusRow>
        ) : (
          <Muted>{text.nothing_ahead}</Muted>
        )}
        {openPolls.map((poll) => (
          <StatusRow key={poll._id}>
            <Name>{text.open_poll(poll.holiday?.title || text.holiday_deleted)}</Name>
            <Sub>{pollCounts(text, poll)}</Sub>
            <Sub>{text.voting_closes(prettyTime(poll.cutoffAt))}</Sub>
          </StatusRow>
        ))}
        {lastClosedPoll && (
          <StatusRow>
            <Name>
              {text.last_closed_poll(lastClosedPoll.holiday?.title || text.holiday_deleted)}
            </Name>
            <Sub>{pollCounts(text, lastClosedPoll)}</Sub>
            {lastClosedPoll.closedAt && (
              <Sub>{text.closed_at(prettyTime(lastClosedPoll.closedAt))}</Sub>
            )}
          </StatusRow>
        )}
      </Section>
    </>
  );
};

const Header = styled.header`
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.17);
  background-color: #ffffff;
  height: 72px;
  position: fixed;
  z-index: 110;
  width: 100%;
  top: 0;
  max-width: 552px;
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

const HeaderText = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #21272e;
`;

const IconWrap = styled.div`
  min-width: 24px;
  height: 24px;
  margin-right: 24px;
  cursor: pointer;
`;

// Status lines wrap: a truncated date or holiday name is the part being read.
const StatusRow = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid #eef0f2;

  & > div {
    white-space: normal;
  }
`;

const Warning = styled.div`
  font-size: 12px;
  color: #eb5757;
`;

const Content = styled.div`
  padding: 0 16px 96px;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SectionName = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #21272e;
  margin-bottom: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #7d8895;
  margin-bottom: 8px;
`;

const Toggle = styled.button`
  border: none;
  background: none;
  padding: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #2f80ed;
  cursor: pointer;
`;

const Muted = styled.div`
  font-size: 14px;
  color: #b4babf;
  padding: 8px 0;
`;

const Row = styled.div<{ isPast?: boolean }>`
  opacity: ${({ isPast }) => (isPast ? 0.5 : 1)};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 0;
  border-bottom: 1px solid #eef0f2;
`;

const EditRow = styled(Row)`
  align-items: flex-end;
  padding-top: 16px;
`;

const Who = styled.div`
  flex: 1 1 160px;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 15px;
  color: #21272e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.div`
  font-size: 12px;
  color: #7d8895;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Action = styled.button`
  border: none;
  background: none;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 700;
  color: #2f80ed;
  cursor: pointer;

  &:disabled {
    color: #b4babf;
    cursor: default;
  }
`;

const Labelled = styled.div`
  flex: 1 1 140px;
  min-width: 0;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  color: #7d8895;
  margin-bottom: 2px;
`;

const Field = styled.input`
  flex: 1 1 140px;
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  font-size: 14px;
  color: #21272e;
`;

export default Holidays;
